/* eslint-disable max-lines */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BadgeCheck,
  CloudUpload,
  Files,
  Globe,
  Languages,
  Lock,
  Users,
  Zap
} from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Switch from '@components/atoms/ui-kit/switch.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { useGetExportTemplates } from '@utils/api/organizations/use-get-export-templates.tsx';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { Template } from '@utils/types/organization/export-template.ts';
import { LabelValueSelectInput } from '@utils/types/shared/label-value-select-input.ts';
import { Language } from '@utils/types/shared/language.ts';
import { DocumentTypeOptions } from '@utils/types/editors/utils.ts';
import { DetectionState } from '@utils/hooks/use-language-detection.ts';
import { Action } from './upload-state';

const MUL_LANGUAGES = [
  { code: 'bg', name: 'Bulgarian' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'de', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fr', name: 'French' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'it', name: 'Italian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lv', name: 'Latvian' },
  { code: 'mt', name: 'Maltese' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'sv', name: 'Swedish' },
  { code: 'uk', name: 'Ukrainian' }
];

interface Props {
  type: DocumentTypeOptions | 'align' | '';
  activeType: 'document' | 'caption';
  // Language
  selectedLanguage: Language | null;
  languageOptions: {
    label: string;
    value: string;
    is_private: boolean;
    keyCode: string;
    name: string;
  }[];
  detection: DetectionState;
  detectedMatch: Language | null;
  userOverrodeLanguageRef: React.MutableRefObject<boolean>;
  dispatch: React.Dispatch<Action>;
  models: { result?: Language[] } | undefined;
  // Speaker detection
  diarization: boolean;
  setDiarization: (v: boolean) => void;
  speakerValidationError: string;
  setSpeakerValidationError: (v: string) => void;
  minSpeakers: number | null;
  maxSpeakers: number | null;
  // Upload method
  useDirectAzureUpload: boolean;
  setUseDirectAzureUpload: (v: boolean) => void;
  hasLargeFiles: boolean;
  hasUrlFiles: boolean;
  // Folder
  selectedFolderId: number | null;
  setSelectedFolderId: (id: number | null) => void;
  folderOptions: { label: string; value: string }[];
  // Status + actions
  isRecording: boolean;
  uploading: boolean;
  azureBlobProgress: number;
  isSubmitting: boolean;
  hasActiveTusUpload: boolean;
  primaryUploadDisabled: boolean;
  alignmentMediaFile: File | null;
  alignmentTranscriptFile: File | null;
  onUpload: () => void;
  getNonAlignButtonLabel: () => string;
}

export function UploadSettingsPanel({
  type,
  activeType,
  selectedLanguage,
  languageOptions,
  detection,
  detectedMatch,
  userOverrodeLanguageRef,
  dispatch,
  models,
  diarization,
  setDiarization,
  speakerValidationError,
  setSpeakerValidationError,
  minSpeakers,
  maxSpeakers,
  useDirectAzureUpload,
  setUseDirectAzureUpload,
  hasLargeFiles,
  hasUrlFiles,
  selectedFolderId,
  setSelectedFolderId,
  folderOptions,
  isRecording,
  uploading,
  azureBlobProgress,
  isSubmitting,
  hasActiveTusUpload,
  primaryUploadDisabled,
  alignmentMediaFile,
  alignmentTranscriptFile,
  onUpload,
  getNonAlignButtonLabel
}: Props) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const [mulModalOpen, setMulModalOpen] = useState(false);
  const { hasFeature } = useEntitlements();
  const { data: exportTemplatesData } = useGetExportTemplates();
  const exportTemplates = exportTemplatesData?.result;

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const showAutoExport =
    type !== 'align' &&
    hasFeature('templates_export') &&
    exportTemplates &&
    exportTemplates.length > 0;

  const showDiarization = type !== 'align' && activeType !== 'caption';
  const showUploadMethod = type !== 'align';

  // 3-column top row: language | auto-export (if enabled) | folder
  // 2-column bottom row: speaker detection | upload method
  const topColCount = showAutoExport ? 3 : 2;

  return (
    <div className="space-y-3">
      {/* Top row: Language + Auto Export + Folder */}
      <div
        className={`grid grid-cols-1 gap-3 ${topColCount === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}
      >
        {/* Language */}
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Languages
              className="w-3.5 h-3.5"
              style={{ color: getColour('inactiveText') }}
            />
            <label
              className="text-xs font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('select-language')}
            </label>
            {detection.status === 'detecting' && (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300 ring-1 ring-indigo-500/20">
                <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-current border-r-transparent animate-spin" />
                {t('detecting-language')}
              </span>
            )}
            {detection.status === 'detected' &&
              detectedMatch &&
              !userOverrodeLanguageRef.current && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 ring-1 ring-emerald-500/20">
                  <BadgeCheck className="w-3 h-3" />
                  {t('language-detected')}
                  {detection.status === 'detected' && (
                    <span className="opacity-70">
                      · {Math.round(detection.confidence * 100)}%
                    </span>
                  )}
                </span>
              )}
          </div>
          <SelectInput
            value={
              selectedLanguage
                ? ({
                    label: `${selectedLanguage.name} (${selectedLanguage.key})`,
                    value: selectedLanguage.id.toString(),
                    is_private: selectedLanguage.is_private
                  } as LabelValueSelectInput & { is_private: boolean })
                : null
            }
            onChange={(opt: { label: string; value: string } | null) => {
              userOverrodeLanguageRef.current = true;
              if (!opt) {
                dispatch({ type: 'SET_SELECTED_LANGUAGE', payload: null });
                return;
              }
              const found =
                models?.result?.find(
                  (item: Language) => item.id.toString() === opt.value
                ) || null;
              dispatch({ type: 'SET_SELECTED_LANGUAGE', payload: found });
            }}
            options={[
              ...languageOptions.filter(o => o.keyCode === 'mul'),
              ...languageOptions.filter(o => o.keyCode !== 'mul')
            ]}
            renderOption={option => {
              const opt = option as unknown as {
                label: string;
                is_private: boolean;
              };
              return (
                <div className="flex items-center gap-2">
                  {opt.is_private ? (
                    <Lock className="w-4 h-4 text-orange-500" />
                  ) : (
                    <Globe className="w-4 h-4 text-green-500" />
                  )}
                  <span className="truncate">{opt.label}</span>
                </div>
              );
            }}
            placeholder={t('select-language')}
            menuPlacement="top"
          />
          {selectedLanguage?.key === 'mul' && (
            <button
              type="button"
              onClick={() => setMulModalOpen(true)}
              className="ml-1 mt-1.5 text-xs underline underline-offset-2 hover:opacity-75 transition-opacity"
              style={{ color: getColour('activeText') }}
            >
              {t('mul-supported-languages') || 'Supported languages'} (
              {MUL_LANGUAGES.length})
            </button>
          )}
        </div>

        {/* Auto Export (conditional) */}
        {showAutoExport && (
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Files
                className="w-3.5 h-3.5"
                style={{ color: getColour('inactiveText') }}
              />
              <label
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('auto-export') || 'Auto Export'}
              </label>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-2xl"
                style={{
                  backgroundColor: pick('rgb(219 234 254)', 'rgb(30 58 138)'),
                  color: pick('rgb(29 78 216)', 'rgb(147 197 253)')
                }}
              >
                {t('optional') || 'Optional'}
              </span>
            </div>
            <SelectInput
              value={null}
              onChange={(opt: { label: string; value: string } | null) => {
                if (!opt) {
                  dispatch({
                    type: 'SET_SELECTED_EXPORT_TEMPLATE',
                    payload: null
                  });
                  return;
                }
                const found =
                  exportTemplates?.find(
                    (item: Template) => item.id?.toString() === opt.value
                  ) || null;
                dispatch({
                  type: 'SET_SELECTED_EXPORT_TEMPLATE',
                  payload: found
                });
              }}
              options={
                exportTemplates?.map((template: Template) => ({
                  label: template.name,
                  value: template.id?.toString() || '',
                  type: template.template_type || template.type
                })) || []
              }
              renderOption={option => {
                const opt = option as unknown as {
                  label: string;
                  type?: string;
                };
                return (
                  <div className="flex items-center gap-2">
                    <span className="truncate">{opt.label}</span>
                    {opt.type && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-2xl"
                        style={{
                          backgroundColor:
                            opt.type === 'SUMMARIZATION'
                              ? pick('rgb(254 243 199)', 'rgb(120 53 15)')
                              : pick('rgb(220 252 231)', 'rgb(20 83 45)'),
                          color:
                            opt.type === 'SUMMARIZATION'
                              ? pick('rgb(146 64 14)', 'rgb(253 230 138)')
                              : pick('rgb(22 101 52)', 'rgb(187 247 208)')
                        }}
                      >
                        {opt.type === 'SUMMARIZATION'
                          ? t('summary') || 'Summary'
                          : t('verbatim') || 'Verbatim'}
                      </span>
                    )}
                  </div>
                );
              }}
              placeholder={
                t('select-export-template') || 'Select template (optional)'
              }
              menuPlacement="top"
              isClearable
              compactOptions
            />
          </div>
        )}

        {/* Save to folder */}
        <div>
          <label
            className="block text-xs font-medium mb-1.5"
            style={{ color: getColour('normalText') }}
          >
            {t('save-to-folder') || 'Save to folder'}
          </label>
          <SelectInput
            value={
              selectedFolderId === null
                ? { label: 'Home (Root)', value: '' }
                : (folderOptions.find(
                    o => o.value === String(selectedFolderId)
                  ) ?? { label: 'Home (Root)', value: '' })
            }
            onChange={(opt: { label: string; value: string } | null) =>
              setSelectedFolderId(opt?.value ? parseInt(opt.value, 10) : null)
            }
            options={folderOptions}
            placeholder={t('select-folder') || 'Select folder'}
            menuPlacement="top"
            isClearable={false}
          />
        </div>
      </div>

      {/* Bottom row: Speaker detection + Upload method */}
      {(showDiarization || showUploadMethod) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Speaker detection */}
          {showDiarization && (
            <div
              className="p-3 rounded-2xl"
              style={{
                backgroundColor: pick(
                  'rgba(0, 0, 0, 0.02)',
                  'rgba(255, 255, 255, 0.02)'
                ),
                border: `1px solid ${getColour('border')}`
              }}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Users
                  className="w-3.5 h-3.5"
                  style={{ color: getColour('inactiveText') }}
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('speaker-detection') || 'Speaker Detection'}
                </span>
              </div>
              <Switch
                id="diarization"
                name="diarization"
                label={t('separate-speakers')}
                checked={diarization}
                onChange={() => setDiarization(!diarization)}
              />
              {diarization && (
                <div
                  className="mt-3 pt-3 border-t"
                  style={{ borderColor: getColour('border') }}
                >
                  <label
                    className="block text-xs font-medium mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('speaker-count-hint') || 'Speaker Count (Optional)'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      id="minSpeakers"
                      name="minSpeakers"
                      type="number"
                      numeric={true}
                      value={minSpeakers ?? ''}
                      onChange={e => {
                        const val = e.target.value;
                        dispatch({
                          type: 'SET_MIN_SPEAKERS',
                          payload: val === '' ? null : parseInt(val, 10)
                        });
                        setSpeakerValidationError('');
                      }}
                      placeholder={t('min-speakers') || 'Min'}
                      size="small"
                      wrapperClass="w-full"
                      inputMode="numeric"
                    />
                    <Input
                      id="maxSpeakers"
                      name="maxSpeakers"
                      type="number"
                      numeric={true}
                      value={maxSpeakers ?? ''}
                      onChange={e => {
                        const val = e.target.value;
                        dispatch({
                          type: 'SET_MAX_SPEAKERS',
                          payload: val === '' ? null : parseInt(val, 10)
                        });
                        setSpeakerValidationError('');
                      }}
                      placeholder={t('max-speakers') || 'Max'}
                      size="small"
                      wrapperClass="w-full"
                      inputMode="numeric"
                    />
                  </div>
                  {speakerValidationError && (
                    <p
                      className="text-xs mt-1.5"
                      style={{
                        color: pick('rgb(239 68 68)', 'rgb(248 113 113)')
                      }}
                    >
                      {speakerValidationError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Upload method */}
          {showUploadMethod && (
            <div
              className="p-3 rounded-2xl"
              style={{
                backgroundColor: pick(
                  'rgba(0, 0, 0, 0.02)',
                  'rgba(255, 255, 255, 0.02)'
                ),
                border: `1px solid ${getColour('border')}`
              }}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Zap
                  className="w-3.5 h-3.5"
                  style={{ color: getColour('inactiveText') }}
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('upload-method') || 'Upload Method'}
                </span>
              </div>
              <Switch
                id="directAzureUpload"
                name="directAzureUpload"
                label={t('direct-upload-recommended')}
                checked={useDirectAzureUpload}
                onChange={() => setUseDirectAzureUpload(!useDirectAzureUpload)}
                disabled={hasLargeFiles || hasUrlFiles}
              />
              {hasLargeFiles && (
                <p
                  className="text-xs mt-1.5"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('direct-upload-disabled-large-file')}
                </p>
              )}
              {hasUrlFiles && !hasLargeFiles && (
                <p
                  className="text-xs mt-1.5"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('direct-upload-required-for-url') ||
                    'Direct upload is required when using a pasted link.'}
                </p>
              )}
              {useDirectAzureUpload && !hasLargeFiles && !hasUrlFiles && (
                <div
                  className="mt-2 p-2.5 rounded-2xl flex gap-2"
                  style={{
                    backgroundColor: pick(
                      'rgba(245, 158, 11, 0.08)',
                      'rgba(251, 191, 36, 0.08)'
                    ),
                    border: `1px solid ${pick('rgba(245, 158, 11, 0.25)', 'rgba(251, 191, 36, 0.25)')}`
                  }}
                >
                  <Zap className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700 dark:text-amber-400" />
                  <div className="flex flex-col gap-0.5">
                    <span
                      className="text-xs font-semibold"
                      style={{ color: getColour('normalText') }}
                    >
                      {t('direct-upload-off-warning-title')}
                    </span>
                    <span
                      className="text-xs leading-relaxed"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('direct-upload-off-warning-body')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recording indicator */}
      {type !== 'align' && isRecording && (
        <div
          className="p-2.5 rounded-2xl flex items-center gap-2"
          style={{
            backgroundColor: pick(
              'rgba(239, 68, 68, 0.08)',
              'rgba(248, 113, 113, 0.08)'
            ),
            border: `1px solid ${pick('rgba(239, 68, 68, 0.2)', 'rgba(248, 113, 113, 0.2)')}`
          }}
        >
          <span
            className="inline-block w-2 h-2 rounded-full animate-pulse"
            style={{
              backgroundColor: pick('rgb(239, 68, 68)', 'rgb(248, 113, 113)'),
              boxShadow: `0 0 8px ${pick('rgba(239, 68, 68, 0.5)', 'rgba(248, 113, 113, 0.5)')}`
            }}
          />
          <span
            className="text-sm font-medium"
            style={{ color: pick('rgb(239, 68, 68)', 'rgb(248, 113, 113)') }}
          >
            {t('recording-in-progress')}
          </span>
        </div>
      )}

      {/* Azure upload progress */}
      {type !== 'align' && useDirectAzureUpload && uploading && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span
              className="text-xs font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('uploading') || 'Uploading'}...
            </span>
            <span
              className="text-xs font-semibold"
              style={{ color: pick('rgb(34 197 94)', 'rgb(74 222 128)') }}
            >
              {azureBlobProgress}%
            </span>
          </div>
          <div
            className="w-full h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: getColour('border') }}
          >
            <div
              className="h-full rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${azureBlobProgress}%`,
                backgroundColor: pick('rgb(34 197 94)', 'rgb(74 222 128)'),
                boxShadow: `0 0 8px ${pick('rgba(34, 197, 94, 0.4)', 'rgba(74, 222, 128, 0.4)')}`
              }}
            />
          </div>
        </div>
      )}

      <Modal
        isOpen={mulModalOpen}
        onClose={() => setMulModalOpen(false)}
        size="medium"
        title={`${t('mul-supported-languages') || 'Supported languages'} (${MUL_LANGUAGES.length})`}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm" style={{ color: getColour('normalText') }}>
            {t('mul-supported-languages-intro') ||
              'Both the Multilingual (auto-detect) and Canary models support the same 25 European languages:'}
          </p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
            {MUL_LANGUAGES.map(({ code, name }) => (
              <div key={code} className="flex items-center gap-2">
                <span
                  className="text-xs font-mono w-6 shrink-0"
                  style={{ color: getColour('inactiveText') }}
                >
                  {code}
                </span>
                <span
                  className="text-sm"
                  style={{ color: getColour('normalText') }}
                >
                  {name}
                </span>
              </div>
            ))}
          </div>
          <div
            className="rounded-2xl p-3 text-xs leading-relaxed"
            style={{
              backgroundColor: getColour('baseLayer'),
              color: getColour('inactiveText')
            }}
          >
            {t('mul-not-supported-note') ||
              'Arabic, Hebrew, and CJK scripts (Chinese, Japanese, Korean) are not supported by the multilingual model. For these languages, select the specific language directly.'}
          </div>
        </div>
      </Modal>

      {/* Upload button */}
      <div className="flex justify-end">
        <Button
          onClick={onUpload}
          label={
            type === 'align'
              ? uploading
                ? t('uploading')
                : t('upload')
              : getNonAlignButtonLabel()
          }
          icon={
            !uploading && !hasActiveTusUpload ? (
              <CloudUpload className="w-4 h-4" />
            ) : undefined
          }
          size="medium"
          variant="primary"
          // Button's default is justify-start, which was fine for the wide
          // "Upload" / "Uploading…" labels but visibly hugged the left when
          // the label switched to just "Pause" / "Resume" inside the same
          // ``min-w-[100px]`` frame.
          className="justify-center"
          loading={isSubmitting && !hasActiveTusUpload}
          disabled={
            type === 'align'
              ? isSubmitting || !alignmentMediaFile || !alignmentTranscriptFile
              : hasActiveTusUpload
                ? false
                : primaryUploadDisabled
          }
        />
      </div>
    </div>
  );
}
